import { RESOURCES_NAMES } from "@/const/resource.constants"
import { useCreate, useDelete, useHttp, useUpdate } from "@/hooks/useHttp"
import { useUser } from "@/hooks/useUser"

import type { Config, GetUseMutationOptions, Params } from "@/hooks/useHttp"
import type { OptionScope } from "./usePolicies.types"
import type {
  OptionsSastGuidanceResponse,
  PreviewSastGuidancePayload,
  PreviewSastGuidanceResponse,
  SastGuidanceAPI,
  SastGuidanceByIdResponse,
  SastGuidanceResponse,
  SastResponse,
  ValidateSastGuidancePayload,
  ValidateSastGuidanceResponse
} from "./useSettings.types"

export const useGetSastSettings = <T = SastResponse, S = T>() => {
  const context = useHttp<T, S>(RESOURCES_NAMES.SETTINGS.SAST.ROOT)

  return context
}

export const useUpdateSastSettings = <T = SastResponse>() => {
  const { updateFeatureFlags } = useUser()

  const context = useUpdate<T, unknown>(
    RESOURCES_NAMES.SETTINGS.SAST.ROOT,
    undefined,
    undefined,
    () => ({
      onSuccess: (response) => {
        const { data } = response as SastResponse
        const { codeScanningEnabled } = data

        updateFeatureFlags({ sastScanFeature: codeScanningEnabled })
      }
    })
  )

  return context
}

export const useGetSastGuidance = <T = SastGuidanceResponse, S = T>(
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.ROOT,
    undefined,
    config
  )

  return context
}

export const useGetSastGuidanceById = <T = SastGuidanceByIdResponse, S = T>(
  id: unknown,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.ROOT}/${id}`,
    undefined,
    {
      enabled: !!id && (typeof id === "string" || typeof id === "number"),
      ...config
    }
  )

  return context
}

export const useCreateSastGuidance = <
  T = SastGuidanceResponse,
  S = { sessionId: string; enabled: boolean; name: string }
>(
  options?: GetUseMutationOptions<T, S>
) => {
  const context = useCreate<T, S>(
    RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.ROOT,
    undefined,
    undefined,
    options
  )

  return context
}

export const useUpdateSastGuidance = <
  T = { data: Partial<SastGuidanceAPI>; needle: string },
  S = Partial<SastGuidanceAPI>
>(
  updater?: (oldData: T, newData: S) => T,
  options?: GetUseMutationOptions<T, S>
) => {
  const context = useUpdate<T, S>(
    RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.ROOT,
    undefined,
    updater,
    options
  )

  return context
}

export const useGetOptionsSastGuidance = <
  T = OptionsSastGuidanceResponse,
  S = T
>(
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.OPTIONS,
    undefined,
    config
  )

  return context
}

export const useGetScopeOptionsSastGuidance = <
  T = KosmosApi.Response<OptionScope[]>,
  S = T
>(
  params?: Params
) => {
  const context = useHttp<T, S>(
    RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.SCOPE_OPTIONS,
    params
  )

  return context
}

export const useValidateSastGuidance = <
  T = ValidateSastGuidanceResponse,
  S = ValidateSastGuidancePayload
>() => {
  const context = useCreate<T, S>(
    RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.VALIDATE
  )

  return context
}

export const useCreatePreviewSastGuidance = <
  T = PreviewSastGuidanceResponse,
  S = PreviewSastGuidancePayload
>() => {
  const context = useCreate<T, S>(
    RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.PREVIEW
  )

  return context
}

export const useDeleteSastGuidance = <
  T = SastGuidanceResponse,
  S = string
>() => {
  const context = useDelete<T, S>(RESOURCES_NAMES.SETTINGS.SAST.GUIDANCE.ROOT)

  return context
}
